/**
 * validarPrompt.js — porta JS EXATA de `validar_prompt`
 * (services/shared/navaflow_shared/agentes/prompt_especialista.py, A1 FR-1..FR-4).
 *
 * Serve ao checklist em tempo real do editor de prompt (Squad Designer e Catálogo).
 * A régua é a mesma do servidor: cinco seções por cabeçalho Markdown (#, ##, ###),
 * mínimo de caracteres e a regra de stack (papel genérico × especialista).
 * A paridade é travada em `src/__tests__/validarPromptParidade.test.js`, sobre os
 * mesmos casos JSON gerados pelo Python (`src/lib/__fixtures__/validar_prompt_casos.json`).
 *
 * Detalhes de paridade com o `re`/`str` do Python (unicode):
 *   - `str.strip()` e `\s` do Python usam `str.isspace()` → classe PY_WS abaixo
 *     (o `\s` do JS inclui U+FEFF e não inclui U+001C..U+001F nem U+0085);
 *   - `.` do Python casa tudo menos `\n` (o do JS também recusa \r, U+2028, U+2029);
 *   - `\d` e `\b` do Python são unicode → `\p{Nd}` e bordas com `[\p{L}\p{N}_]`;
 *   - `len()` conta code points → `Array.from(t).length`.
 *
 * Função pura, sem I/O.
 */

export const SECOES = ['papel', 'stack', 'qualidade', 'proibido', 'saida'];

export const MINIMO_PADRAO = 1200;
export const MINIMO_ABSOLUTO = 200;

/** Rótulos legíveis das cinco seções (checklist da tela). */
export const ROTULO_DA_SECAO = {
  papel: 'Papel e escopo',
  stack: 'Stack e convenções',
  qualidade: 'Critérios de qualidade',
  proibido: 'Proibido',
  saida: 'Formato de saída',
};

/** Texto pt-BR de cada motivo devolvido pela validação. */
export const TEXTO_DO_MOTIVO = {
  abaixo_do_minimo: 'abaixo do mínimo de caracteres',
  papel_generico_cita_tecnologia: 'papel genérico cita tecnologia — a stack é do blueprint',
  stack_sem_blueprint: 'a seção de stack não diz que a stack vem do blueprint',
  tecnologia_sem_precedencia_do_blueprint:
    'cita tecnologia sem declarar que o blueprint prevalece',
};

/** Os papéis de camada da 0076/0088 — a stack deles é SEMPRE a do blueprint. */
export const PAPEIS_GENERICOS = new Set([
  'backend-developer', 'frontend-developer', 'data-engineer', 'devops-engineer',
  'test-automator', 'mobile-developer', 'documentation-engineer',
]);

// Espaço em branco segundo `str.isspace()` do Python.
const PY_WS = '\\t\\n\\v\\f\\r\\x1c-\\x1f \\x85\\xa0\\u1680\\u2000-\\u200a\\u2028\\u2029\\u202f\\u205f\\u3000';
const RE_PONTAS = new RegExp(`^[${PY_WS}]+|[${PY_WS}]+$`, 'gu');
const RE_SO_ESPACO = new RegExp(`^[${PY_WS}]*$`, 'u');

/** `str.strip()` do Python (sem argumentos). */
function pyStrip(s) {
  return s.replace(RE_PONTAS, '');
}

/** UTF-8 sem BOM e quebra `\n`, sem espaço sobrando nas pontas (EC-5). */
export function normalizar(texto) {
  let t = (texto || '').replace(/﻿/g, '');
  t = t.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  return pyStrip(t);
}

const _SINONIMOS = {
  papel: ['papel e escopo', 'quem voce e', 'papel', 'escopo'],
  stack: ['stack e convencoes', 'convencoes', 'stack'],
  qualidade: ['criterios de qualidade', 'definition of done', 'qualidade', 'dod'],
  proibido: ['o que e proibido', 'nao faca', 'proibido', 'nunca'],
  saida: ['formato de saida', 'entregaveis', 'saida'],
};

// `^(#{1,3})\s+(.+?)\s*#*\s*$` — com o \s e o . do Python.
const _CABECALHO = new RegExp(`^(#{1,3})[${PY_WS}]+([^\\n]+?)[${PY_WS}]*#*[${PY_WS}]*$`, 'u');

function _semAcento(s) {
  return s.normalize('NFD').replace(/\p{Mn}/gu, '');
}

function _escapar(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// `t.strip(" :*_`")`
function _stripChars(s, chars) {
  let ini = 0;
  let fim = s.length;
  while (ini < fim && chars.includes(s[ini])) ini += 1;
  while (fim > ini && chars.includes(s[fim - 1])) fim -= 1;
  return s.slice(ini, fim);
}

const _RE_NUMERACAO = new RegExp(`^\\p{Nd}+[.)]?[${PY_WS}]*`, 'u');

/** A seção que o título representa, ou null. Aceita numeração e complemento. */
function _chaveDoCabecalho(titulo) {
  let t = pyStrip(_semAcento(titulo).toLowerCase());
  t = t.replace(_RE_NUMERACAO, '');   // "1. Papel" → "papel"
  t = _stripChars(t, ' :*_`');
  for (const chave of SECOES) {
    for (const s of _SINONIMOS[chave]) {
      if (t === s || new RegExp(`^${_escapar(s)}(?![a-z0-9])`, 'u').test(t)) return chave;
    }
  }
  return null;
}

function _casaCabecalho(linha) {
  return _CABECALHO.exec(pyStrip(linha));
}

/** Seção presente = cabeçalho reconhecido com corpo não vazio até o próximo cabeçalho de seção. */
function _secoesPresentes(texto) {
  const linhas = texto.split('\n');
  const marcas = [];   // [linha, nível, chave]
  linhas.forEach((linha, i) => {
    const m = _casaCabecalho(linha);
    if (m) marcas.push([i, m[1].length, _chaveDoCabecalho(m[2])]);
  });
  const presentes = Object.fromEntries(SECOES.map((s) => [s, false]));
  marcas.forEach(([i, nivel, chave], idx) => {
    if (chave === null) return;
    let fim = linhas.length;
    for (const [j, nivelJ, chaveJ] of marcas.slice(idx + 1)) {
      if (chaveJ !== null || nivelJ <= nivel) { fim = j; break; }
    }
    const corpo = linhas.slice(i + 1, fim).filter((l) => !_casaCabecalho(l));
    if (!RE_SO_ESPACO.test(corpo.join('\n'))) presentes[chave] = true;
  });
  return presentes;
}

/** O texto da seção `chave` (vazio se ausente). */
function _corpoDaSecao(texto, chave) {
  let dentro = false;
  const corpo = [];
  for (const linha of texto.split('\n')) {
    const m = _casaCabecalho(linha);
    if (m) {
      const k = _chaveDoCabecalho(m[2]);
      if (k !== null) { dentro = k === chave; continue; }
    }
    if (dentro) corpo.push(linha);
  }
  return corpo.join('\n');
}

// Mesma lista da 0076 (`tecnologias_prescritas`).
const _PADROES_DE_STACK = [
  'node(?:\\.js)?', 'python', 'golang', `go(?=[${PY_WS}]*\\p{Nd})`, 'java(?!script)',
  'javascript', 'typescript', 'c#', 'c\\+\\+', '\\.net', 'dotnet',
  `react(?:[${PY_WS}]+native)?`, 'vue', 'angular', 'svelte', 'next\\.?js',
  'spring', 'django', 'flask', 'fastapi', 'rails', 'laravel',
  'php', 'ruby', 'rust', 'kotlin', 'swift', 'scala', 'elixir',
  'sql', 'nosql', 'postgres(?:ql)?', 'mysql', 'mongo(?:db)?',
  'redis', 'oracle', 'sqlite', 'kafka', 'rabbitmq',
  'docker', 'kubernetes', 'k8s', 'terraform', 'ansible', 'jenkins',
  'aws', 'azure', 'gcp', 'graphql', 'grpc', 'flutter', 'android',
];
const _REGEX_STACK = new RegExp(
  `(?<![0-9A-Za-zÀ-ÿ])(?:${_PADROES_DE_STACK.join('|')})(?![0-9A-Za-zÀ-ÿ])`, 'giu',
);

// `\b` unicode do Python, emulado com a classe de palavra [\p{L}\p{N}_].
const _W = '[\\p{L}\\p{N}_]';
const _PRECEDENCIA_DO_BLUEPRINT = new RegExp(
  `blueprint[^.\\n]{0,80}(?<!${_W})(?:prevalece|vence|manda|tem prioridade|prevails|wins)(?!${_W})`,
  'iu',
);

/** Tecnologias citadas, em ordem e sem repetição (a regra da 0076). */
export function tecnologiasPrescritas(texto) {
  const vistos = [];
  for (const m of (texto || '').matchAll(_REGEX_STACK)) {
    const chave = m[0].toLowerCase();
    if (!vistos.includes(chave)) vistos.push(chave);
  }
  return vistos;
}

/**
 * Valida a estrutura de um prompt de especialista.
 * `ok` só com as cinco seções, `chars >= minimo` e nenhum motivo de stack.
 *
 * @param {string} texto
 * @param {{ papelGenerico: boolean, minimo?: number }} opts
 * @returns {{ ok: boolean, chars: number, minimo: number,
 *   secoes: Record<string, boolean>, faltando: string[], motivos: string[] }}
 */
export function validarPrompt(texto, { papelGenerico, minimo = MINIMO_PADRAO } = {}) {
  const t = normalizar(texto);
  const secoes = _secoesPresentes(t);
  const faltando = SECOES.filter((s) => !secoes[s]);
  const motivos = [];
  const chars = Array.from(t).length;
  if (chars < minimo) motivos.push('abaixo_do_minimo');
  const techs = tecnologiasPrescritas(t);
  if (papelGenerico) {
    if (techs.length) motivos.push('papel_generico_cita_tecnologia');
    if (secoes.stack && !_corpoDaSecao(t, 'stack').toLowerCase().includes('blueprint')) {
      motivos.push('stack_sem_blueprint');
    }
  } else if (techs.length && !_PRECEDENCIA_DO_BLUEPRINT.test(t)) {
    motivos.push('tecnologia_sem_precedencia_do_blueprint');
  }
  return {
    ok: !faltando.length && !motivos.length,
    chars, minimo, secoes, faltando, motivos,
  };
}

/** `especialista` (ok) · `incompleto` (há texto, falta algo) · `sem_prompt` (vazio). */
export function statusDoPrompt(texto, opts) {
  if (!normalizar(texto || '')) return 'sem_prompt';
  return validarPrompt(texto || '', opts).ok ? 'especialista' : 'incompleto';
}

/** É papel genérico de camada (a stack vem sempre do blueprint)? */
export function ehPapelGenerico(slug) {
  return PAPEIS_GENERICOS.has(String(slug || '').trim());
}
